import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { __removeTimezone } from '@zocdoc-frontend-common/components-timesgrid/src/utils/date-helpers';
import { increment } from '@zocdoc-frontend-common/utils-react';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const getApptTimeWithMaybeTZ = (
    appointmentTimeString,
    appointmentLocationTimeZone
) => {
    if (appointmentTimeString) {
        try {
            const apptTime = dayjs(appointmentTimeString);
            if (appointmentLocationTimeZone) {
                return apptTime.tz(appointmentLocationTimeZone);
            }
            return apptTime;
        } catch {
            /* ERROR 1
             * dayjs.tz breaks on IE because it doesn't support the toLocaleString
             * method of Date that dayjs invokes (see https://stackoverflow.com/a/54365285).
             * To handle this we fallback to just comparing the raw time
             * after stripping the UTC offset with the current time in the
             * browser process, assuming the patient is using their browser
             * in the same time zone as the appointment
             */
            increment({ name: 'booking_web_app.time_zone_error' });
            return dayjs(__removeTimezone(appointmentTimeString));
        }
    }
};

const tryFormatApptTime = (apptTime, format) => {
    try {
        return apptTime.format(format);
    } catch {
        /* ERROR 2
         * dayjs.tz breaks on many years-old Chrome versions with an error like
         * (intermediate value)(...).formatToParts is not a function
         */
        increment({ name: 'booking_web_app.format_error' });
        return null;
    }
};

export function formatAppointmentTimeForDisplay(
    appointmentTimeString,
    appointmentLocationTimeZone
) {
    if (appointmentTimeString) {
        const apptTime = getApptTimeWithMaybeTZ(
            appointmentTimeString,
            appointmentLocationTimeZone
        );
        return tryFormatApptTime(apptTime, 'ddd, MMM D - h:mm a z');
    }
}

export function formatAppointmentTimeTodayTomorrow(
    appointmentTimeString,
    appointmentLocationTimeZone
) {
    if (appointmentTimeString) {
        const endOfToday = dayjs().endOf('day');
        const endOfTomorrow = dayjs().endOf('day').add(1, 'days');

        const appointmentTime = getApptTimeWithMaybeTZ(
            appointmentTimeString,
            appointmentLocationTimeZone
        );

        if (appointmentTime.isBefore(endOfToday)) {
            return tryFormatApptTime(
                appointmentTime,
                '[Today], MMM D - h:mm a z'
            );
        } else if (appointmentTime.isBefore(endOfTomorrow)) {
            return tryFormatApptTime(
                appointmentTime,
                '[Tomorrow], MMM D - h:mm a z'
            );
        } else {
            return tryFormatApptTime(appointmentTime, 'ddd, MMM D - h:mm a z');
        }
    }
}

export function formatAppointmentDetailsTime(
    appointmentTimeString,
    appointmentLocationTimeZone
) {
    if (appointmentTimeString) {
        const appointmentTime = getApptTimeWithMaybeTZ(
            appointmentTimeString,
            appointmentLocationTimeZone
        );
        return tryFormatApptTime(appointmentTime, 'h:mma z');
    }
}

export function formatAppointmentDetailsDate(
    appointmentTimeString,
    appointmentLocationTimeZone
) {
    if (appointmentTimeString) {
        const appointmentTime = getApptTimeWithMaybeTZ(
            appointmentTimeString,
            appointmentLocationTimeZone
        );
        return tryFormatApptTime(appointmentTime, 'MMMM Do');
    }
}

export function formatAppointmentDateAndTime(
    appointmentTimeString,
    appointmentLocationTimeZone
) {
    if (appointmentTimeString) {
        const appointmentTime = getApptTimeWithMaybeTZ(
            appointmentTimeString,
            appointmentLocationTimeZone
        );
        return {
            date: tryFormatApptTime(appointmentTime, 'dddd, MMM D'),
            time: tryFormatApptTime(appointmentTime, 'h:mma z'),
        };
    } else {
        return {};
    }
}
